import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { CustomerSettings } from '../@types';
import type { WalletTransaction } from '../@types/wallet';
import { QUERY_KEYS } from '../constants';
import { LEDGER_STATUS, TRANSACTION_TYPE } from '../types/enums';
import { useGetWalletTransactions, useMarkFirstDepositGtmEventSent, useUserData } from '../queries';
import useMarketplaceCurrency from './usePlayerCurrency';
import { buildFirstDepositEvent, isNeoplay24Origin, pushDataLayerEvent } from '../utils/gtm';

type WalletTransactionItem = ReturnType<typeof useGetWalletTransactions>['items'][number];

// In-memory guard: once we have pushed the event for a user in this session we never
// push again, even if the backend flag update fails. This is the session-level
// safety net described in the feature spec.
const trackedUserIds = new Set<string>();

// Logged-once-per-session guard for when a first deposit is detected but the current
// domain is not the GTM tracking domain (e.g. dev/stg). Lets us verify detection without
// spamming the console on every render.
const domainBlockedLoggedUserIds = new Set<string>();

const isTrackableCompletedDeposit = (
  transaction: WalletTransactionItem | undefined
): transaction is WalletTransaction => {
  return (
    !!transaction &&
    typeof transaction.id === 'string' &&
    typeof transaction.amount === 'number' &&
    transaction.type === TRANSACTION_TYPE.DEPOSIT &&
    // The endpoint already filters by completed status; re-check defensively.
    (transaction.status === undefined || transaction.status === LEDGER_STATUS.COMPLETED)
  );
};

export const useFirstDepositTracking = () => {
  const isTrackingDomain = isNeoplay24Origin();
  const { isAuthenticated, userId } = useAuth();
  const marketplaceCurrency = useMarketplaceCurrency();
  const queryClient = useQueryClient();
  const markFirstDepositGtmEventSent = useMarkFirstDepositGtmEventSent();

  const { data: user } = useUserData();

  // Only new users (server flag explicitly false) are eligible. Existing users were
  // backfilled to `true` by the api-crm migration and never reach this branch.
  // The domain is intentionally NOT part of this gate: we still fetch on non-tracking
  // origins (dev/stg) so the detection + dispatch path can be verified before the
  // neoplay24-only push. It stays auth-gated, so no requests fire for guests.
  const isEligibleForFirstDepositEvent = isAuthenticated && Boolean(userId) && user?.firstDepositGTMEventSent === false;

  const { items: walletTransactions, isLoading: areTransactionsLoading } = useGetWalletTransactions(
    isEligibleForFirstDepositEvent
      ? {
          page: 1,
          limit: 1,
          types: [TRANSACTION_TYPE.DEPOSIT],
          status: LEDGER_STATUS.COMPLETED,
          order: 'ASC',
        }
      : undefined,
    { enabled: isEligibleForFirstDepositEvent }
  );

  const earliestDeposit = walletTransactions?.[0];

  useEffect(() => {
    if (
      !isEligibleForFirstDepositEvent ||
      !userId ||
      areTransactionsLoading ||
      trackedUserIds.has(userId) ||
      !isTrackableCompletedDeposit(earliestDeposit)
    ) {
      return;
    }

    const event = buildFirstDepositEvent(earliestDeposit, earliestDeposit.currency ?? marketplaceCurrency);

    // GTM only fires on the neoplay24 domain. On other origins (dev/stg) surface that the
    // event was detected and would be dispatched, so the mechanism can be verified pre-prod.
    if (!isTrackingDomain) {
      if (!domainBlockedLoggedUserIds.has(userId)) {
        domainBlockedLoggedUserIds.add(userId);
        console.log(
          '[GTM] First deposit detected; event should be dispatched but is blocked by domain restriction',
          event
        );
      }
      return;
    }

    pushDataLayerEvent(event);

    // Mark immediately so a re-render cannot push a second time before the flag persists.
    trackedUserIds.add(userId);

    // Optimistically flip the local profile flag so the effect short-circuits even if the
    // update below is still in flight or fails.
    queryClient.setQueriesData<CustomerSettings>({ queryKey: [QUERY_KEYS.customerData] }, (prev) =>
      prev ? { ...prev, firstDepositGTMEventSent: true } : prev
    );

    markFirstDepositGtmEventSent.mutate();
  }, [
    areTransactionsLoading,
    earliestDeposit,
    isEligibleForFirstDepositEvent,
    isTrackingDomain,
    marketplaceCurrency,
    markFirstDepositGtmEventSent,
    queryClient,
    userId,
  ]);
};
